import { Mutation } from "@apollo/client/react/components";
import { BlockBodyOverflow, Button, Modal, ModalClose } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ISaveVendorSharingMutation,
  ISaveVendorSharingMutationVariables,
} from "~/lib/mutations/__generated__/SaveVendorSharing.generated";
import { ICompany, ICompanySharingOptions } from "~/types/types";

import filter from "lodash/fp/filter";
import identity from "lodash/fp/identity";
import omit from "lodash/fp/omit";
import { ReactElement, useState } from "react";
import Switch from "react-switch";
import { useToggle } from "react-use";
import getVendorSharingUrl from "~/components/vendor/profile/owner/tabs/getVendorSharingUrl";
import SaveVendorSharing from "~/lib/mutations/SaveVendorSharing";

interface IEditVendorSharingModal {
  vendor: Pick<ICompany, "_id" | "slug" | "enabledSharing" | "sharing">;
  isOpen: boolean;
  onRequestClose: () => any;
}

type ISharingOptions = Pick<
  ICompanySharingOptions,
  "stories" | "projects" | "products" | "resources" | "groups" | "governments" | "newsworthy" | "highlights" | "team"
>;

interface IToggle {
  value: keyof ISharingOptions;
  description: string | ReactElement;
  sharing: ISharingOptions;
  setSharing: (value?: any) => any;
  enabledSharing: boolean;
}

export default function EditVendorSharingModal({ vendor, isOpen, onRequestClose, ...props }: IEditVendorSharingModal) {
  const intl = useIntl();
  const [enabledSharing, toggleEnabledSharing] = useToggle(vendor.enabledSharing || false);
  const [sharing, setSharing] = useState<ISharingOptions>(omit(["__typename"], vendor.sharing));
  const [saving, setSaving] = useState(false);

  function handleSetSharing(toggleEnabledSharing: (value?: any) => void) {
    return (options: any) => {
      if (filter(identity, options).length === 0) {
        toggleEnabledSharing();
      }

      setSharing(options);
    };
  }

  return (
    <Modal
      {...props}
      isOpen={isOpen}
      onRequestClose={() => {
        if (!saving) {
          onRequestClose();
        }
      }}
      ariaHideApp={false}
      overlayStyle={{
        background: "rgba(255, 255, 255, 0.6)",
      }}
      style={{
        boxShadow: "0 0 20px 0 #e1e1e1",
        border: null,
      }}
    >
      {isOpen && <BlockBodyOverflow />}

      <ModalClose
        onClick={onRequestClose}
        style={{
          cursor: "pointer",
        }}
      />

      <div
        css={{
          padding: 20,
          width: 450,
        }}
      >
        <div
          css={{
            display: "grid",
            rowGap: 15,
          }}
        >
          <div>
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <h3
                css={{
                  display: "flex",
                  alignItems: "center",
                  color: palette.darkGray,
                  margin: 0,
                  fontWeight: 500,
                  fontSize: 21,
                }}
              >
                <FormattedMessage defaultMessage="Sharing" id="XfKNCg" />
                <span
                  css={{
                    marginLeft: 5,
                    fontSize: 14,
                    color: enabledSharing ? "#178B61" : "#B44F4F",
                  }}
                >
                  <Switch
                    uncheckedIcon={
                      <div
                        css={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          fontSize: 15,
                          color: palette.lightRed,
                          paddingRight: 10,
                        }}
                      >
                        off
                      </div>
                    }
                    checkedIcon={
                      <div
                        css={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          fontSize: 15,
                          color: "#fff",
                          paddingLeft: 10,
                        }}
                      >
                        on
                      </div>
                    }
                    handleDiameter={22}
                    height={26}
                    width={65}
                    onChange={(checked) => {
                      toggleEnabledSharing();

                      if (checked) {
                        setSharing({
                          stories: true,
                          projects: true,
                          products: true,
                          resources: true,
                          groups: true,
                          governments: true,
                          newsworthy: true,
                          highlights: true,
                          team: true,
                        });
                      } else {
                        setSharing({
                          stories: false,
                          projects: false,
                          products: false,
                          resources: false,
                          groups: false,
                          governments: false,
                          newsworthy: false,
                          highlights: false,
                          team: false,
                        });
                      }
                    }}
                    checked={enabledSharing}
                    onColor={palette.greenSuccess}
                    offColor={palette.red}
                  />
                </span>
              </h3>
            </div>
          </div>

          {enabledSharing ? (
            <p
              css={{
                color: palette.darkGray,
                fontSize: 14,
                margin: 0,
              }}
            >
              <FormattedMessage
                defaultMessage="Select the items below to display in your public share page"
                id="6kIwcu"
              />
              :{" "}
              <a
                css={{
                  color: "#005AEE",
                }}
                href={getVendorSharingUrl(vendor.slug as string)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {getVendorSharingUrl(vendor.slug as string)}
              </a>
            </p>
          ) : (
            <p
              css={{
                color: palette.darkGray,
                fontSize: 14,
                margin: 0,
              }}
            >
              <FormattedMessage
                defaultMessage="Turn on sharing to create your custom page and then select what you want to share. It is that simple! Your Vendor's unique URL"
                id="/jih0A"
              />
              :{" "}
              <a
                css={{
                  color: "#005AEE",
                }}
                href={getVendorSharingUrl(vendor.slug as string)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {getVendorSharingUrl(vendor.slug as string)}
              </a>
            </p>
          )}

          <div>
            <p
              css={{
                color: palette.darkGray,
                fontSize: 14,
                margin: 0,
                marginBottom: 5,
              }}
            >
              <strong>
                <FormattedMessage defaultMessage="Public" id="uhu5aG" />:
              </strong>{" "}
              <FormattedMessage defaultMessage="Anybody can view (including guests in Govlaunch)" id="dF5LfH" />
            </p>
            <p
              css={{
                color: palette.darkGray,
                fontSize: 14,
                margin: 0,
              }}
            >
              <strong>
                <FormattedMessage defaultMessage="Private" id="viXE32" />:
              </strong>{" "}
              <FormattedMessage defaultMessage="Only verified users in the Govlaunch community can view" id="UH6st0" />
            </p>
          </div>

          <div
            css={{
              opacity: enabledSharing ? 1 : 0.3,
              display: "grid",
              rowGap: 10,
            }}
          >
            <Toggle
              value="stories"
              description={
                <span>
                  <FormattedMessage defaultMessage="Stories" id="Ldxym4" />{" "}
                  <span
                    css={{
                      fontSize: 11,
                    }}
                  >
                    (
                    <FormattedMessage defaultMessage="only stories marked as public will be visible" id="UPxtYU" />)
                  </span>
                </span>
              }
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="projects"
              description={
                <span>
                  <FormattedMessage defaultMessage="Projects" id="UxTJRa" />{" "}
                  <span
                    css={{
                      fontSize: 11,
                    }}
                  >
                    (
                    <FormattedMessage defaultMessage="only projects marked as public will be visible" id="8uWZ5k" />)
                  </span>
                </span>
              }
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="products"
              description={
                <span>
                  <FormattedMessage defaultMessage="Products" id="7NFfmz" />{" "}
                  <span
                    css={{
                      fontSize: 11,
                    }}
                  >
                    (
                    <FormattedMessage defaultMessage="only products marked as public will be visible" id="lMw4W7" />)
                  </span>
                </span>
              }
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="resources"
              description={intl.formatMessage({
                defaultMessage: "Resources",
                id: "c/KktL",
              })}
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="groups"
              description={intl.formatMessage({
                defaultMessage: "Groups",
                id: "hzmswI",
              })}
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="governments"
              description={intl.formatMessage({
                defaultMessage: "Governments",
                id: "qnQX9U",
              })}
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="newsworthy"
              description={intl.formatMessage({
                defaultMessage: "Newsworthy",
                id: "JhItYg",
              })}
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="highlights"
              description={intl.formatMessage({
                defaultMessage: "Highlights",
                id: "KGmQjH",
              })}
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
            <Toggle
              value="team"
              description={intl.formatMessage({
                defaultMessage: "Team Members",
                id: "HCYBYT",
              })}
              sharing={sharing}
              setSharing={handleSetSharing(toggleEnabledSharing)}
              enabledSharing={enabledSharing}
            />
          </div>

          <Mutation<ISaveVendorSharingMutation, ISaveVendorSharingMutationVariables> mutation={SaveVendorSharing}>
            {(saveVendorSharing) => (
              <Button
                disabled={saving}
                size="small"
                color={palette.innovatorBlue}
                css={{
                  userSelect: "none",
                  marginTop: 10,
                }}
                onClick={() => {
                  setSaving(true);
                  saveVendorSharing({
                    variables: {
                      companyId: vendor._id,
                      sharing: {
                        stories: sharing.stories ? sharing.stories : false,
                        projects: sharing.projects ? sharing.projects : false,
                        products: sharing.products ? sharing.products : false,
                        resources: sharing.resources ? sharing.resources : false,
                        groups: sharing.groups ? sharing.groups : false,
                        governments: sharing.governments ? sharing.governments : false,
                        newsworthy: sharing.newsworthy ? sharing.newsworthy : false,
                        highlights: sharing.highlights ? sharing.highlights : false,
                        team: sharing.team ? sharing.team : false,
                      },
                      enabledSharing: enabledSharing,
                    },
                  }).then(() => {
                    setSaving(false);
                    onRequestClose();
                  });
                }}
              >
                {saving ? (
                  <FormattedMessage defaultMessage="Saving" id="QLkzoS" />
                ) : (
                  <FormattedMessage defaultMessage="Save" id="jvo0vs" />
                )}
              </Button>
            )}
          </Mutation>
        </div>
      </div>
    </Modal>
  );
}

function Toggle({ value, description, sharing, setSharing, enabledSharing }: IToggle) {
  return (
    <div>
      <label
        css={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Switch
          uncheckedIcon={
            <div
              css={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                fontSize: 12,
                color: palette.lightRed,
                paddingRight: 15,
              }}
            >
              off
            </div>
          }
          checkedIcon={
            <div
              css={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                fontSize: 12,
                color: "#fff",
                paddingLeft: 15,
              }}
            >
              on
            </div>
          }
          offColor={palette.red}
          handleDiameter={22}
          height={24}
          width={80}
          onColor={palette.greenSuccess}
          onChange={() => {
            return setSharing(
              omit("__typename", {
                ...sharing,
                [value]: !sharing[value],
              }),
            );
          }}
          checked={enabledSharing && Boolean(sharing[value])}
        />
        <span
          css={{
            marginLeft: 10,
            color: palette.darkGray,
            fontSize: 16,
          }}
        >
          {description}
        </span>
      </label>
    </div>
  );
}
